// Deadpan news headlines generated from SimEvents (GDD §13), used by the HUD ticker and the war-report recap. Each
// event kind has one writer (a full mapped Record, so a new kind must decide whether it makes the news). Templates
// are picked deterministically from the event tick, so a replayed war reads the same.

import { BUILDING_KEYS, STRIKE_KEYS, TAX_SETTINGS, UNIT_KEYS, keyOf } from '@taxwar/content'
import { MatchPhase, NEUTRAL_OWNER, NO_SIM_ID, type SimEvent, type SimEventKind } from '@taxwar/net'
import { BUILDING_PRESENTATION, STRIKE_PRESENTATION, STRIKE_QUIPS, TAX_LABELS, UNIT_PRESENTATION, UNIT_QUIPS } from './catalog'
import { HudError } from './errors'
import { formatMoney } from './format'
import type { WarMapIndex } from './warMap'

/** The advisor desk a headline is filed by (GDD §13: Finance Minister, General, Press Secretary). */
export type HeadlineDesk = 'finance' | 'defence' | 'press'

export const DESK_LABELS: Readonly<Record<HeadlineDesk, string>> = {
  finance: 'FINANCE',
  defence: 'DEFENCE',
  press: 'PRESS',
}

export interface Headline {
  readonly tick: number
  readonly desk: HeadlineDesk
  readonly text: string
}

type EventOf<K extends SimEventKind> = Extract<SimEvent, { readonly kind: K }>
type Story = { readonly desk: HeadlineDesk; readonly text: string } | null
type Writer<K extends SimEventKind> = (event: EventOf<K>, map: WarMapIndex) => Story

function pick(templates: readonly string[], tick: number): string {
  const text = templates[tick % templates.length]
  if (text === undefined) throw new HudError('headline template list is empty')
  return text
}

function story(desk: HeadlineDesk, text: string): Story {
  return { desk, text }
}

const PHASE_VALUES: readonly number[] = Object.values(MatchPhase)

export function phaseOf(code: number): MatchPhase {
  if (!PHASE_VALUES.includes(code)) throw new HudError(`unknown match phase code ${code}`)
  return code as MatchPhase
}

const PHASE_HEADLINES: Readonly<Record<MatchPhase, string | null>> = {
  [MatchPhase.setup]: null,
  [MatchPhase.regulation]: null,
  [MatchPhase.overtime]: 'OVERTIME: revenue doubled, sudden death declared; cooking shows pre-empted',
  [MatchPhase.ended]: null,
}

const APPROVAL_HEADLINES: Readonly<Record<EventOf<'approvalThreshold'>['band'], string>> = {
  high: "Approval soars; leader's portrait added to more banknotes",
  normal: 'Public mood settles back to "grudging acceptance"',
  low: 'Protests outside parliament; riot police request overtime pay',
}

const WRITERS: { readonly [K in SimEventKind]: Writer<K> } = {
  // Your deploys say where the doctrine put them and why (GDD §7.1).
  unitDeployed: (e, map) => {
    const key = keyOf(UNIT_KEYS, e.unitType)
    const unit = UNIT_PRESENTATION[key]
    const city = map.cityBySimId(e.city).name
    if (e.owner === map.you) {
      const quip = UNIT_QUIPS[key]
      return story('finance', pick([
        `${unit.name} deployed at ${city} (${formatMoney(e.cost)}). ${quip}`,
        `${unit.sponsor} delivers ${unit.name} to ${city}. ${quip}`,
        `${unit.name} lands in ${city}; receipt printed on 14 metres of paper. ${quip}`,
      ], e.tick))
    }
    return story('defence', pick([
      `${map.nationName(e.owner)} fields ${unit.name} near ${city}; analysts "concerned, but invoicing"`,
      `Satellites spot ${map.nationName(e.owner)} ${unit.name} at ${city}. ${unit.sponsor} declines to comment`,
    ], e.tick))
  },
  // Carrier fighters launch on their own schedule: routine, and the carrier purchase already made the news.
  unitSpawned: () => null,
  unitKilled: (e, map) => {
    const unit = UNIT_PRESENTATION[keyOf(UNIT_KEYS, e.unitType)]
    if (e.owner === map.you) {
      return story('defence', pick([
        `${unit.name} lost in service; replacement already back-ordered`,
        `Ministry: ${unit.name} "strategically converted into scrap"`,
      ], e.tick))
    }
    return story('defence', pick([
      `${map.nationName(e.owner)} ${unit.name} decommissioned ahead of schedule`,
      `${map.nationName(e.owner)} loses ${unit.name}; its warranty expires with it`,
    ], e.tick))
  },
  buildingBuilt: (e, map) => {
    const building = BUILDING_PRESENTATION[keyOf(BUILDING_KEYS, e.buildingType)]
    const city = map.cityBySimId(e.city).name
    if (e.owner === map.you) return story('finance', `${building.name} opens in ${city}; the ribbon cost more than the building`)
    return story('defence', `${map.nationName(e.owner)} opens a ${building.name} in ${city}`)
  },
  buildingDestroyed: (e, map) => {
    const building = BUILDING_PRESENTATION[keyOf(BUILDING_KEYS, e.buildingType)]
    if (e.owner === map.you) return story('finance', `${building.name} demolished; insurers cite the "act of war" clause`)
    return story('defence', `${map.nationName(e.owner)} ${building.name} reduced to a zoning dispute`)
  },
  cityFallen: (e, map) => {
    const city = map.cityBySimId(e.city).name
    if (e.previousOwner === map.you) return story('press', `${city} falls silent; mayor's office "reviewing its options"`)
    return story('defence', `${city} defences collapse; real-estate agents circle`)
  },
  cityRestored: (e, map) => {
    const city = map.cityBySimId(e.city).name
    if (e.owner === map.you) return story('press', `${city} back online; mayor unveils plaque commemorating the plaque`)
    return story('defence', `${map.nationName(e.owner)} re-opens ${city}; the paint is still wet`)
  },
  cityCaptured: (e, map) => {
    const city = map.cityBySimId(e.city).name
    if (e.newOwner === map.you) {
      return story('finance', pick([
        `${map.nationName(e.newOwner)} takes ${city}; property taxes due Monday`,
        `${city} "liberated"; new flags invoiced at cost plus 40%`,
      ], e.tick))
    }
    if (e.previousOwner === map.you) return story('press', `${city} lost to ${map.nationName(e.newOwner)}; officials insist it was "mostly suburbs"`)
    return story('defence', `${map.nationName(e.newOwner)} annexes ${city}; locals asked to update their letterheads`)
  },
  strikeLaunched: (e, map) => {
    const strike = STRIKE_PRESENTATION[keyOf(STRIKE_KEYS, e.strikeType)]
    if (e.owner === map.you) {
      const key = keyOf(STRIKE_KEYS, e.strikeType)
      const from = e.site === NO_SIM_ID ? '' : ` from ${map.cityBySimId(e.site).name}`
      return story('finance', `${strike.name} launched${from} at taxpayer expense (${formatMoney(e.cost)}). ${STRIKE_QUIPS[key]}`)
    }
    return story('defence', `Incoming ${strike.name} from ${map.nationName(e.owner)}; citizens advised to look busy`)
  },
  strikeIntercepted: () => story('defence', 'Missile intercepted mid-air; both sides bill for the fireworks'),
  // The launch already made the news; impacts are VFX, not headlines.
  strikeImpact: () => null,
  sanctionsApplied: (e, map) => {
    if (e.by === map.you) return story('finance', `${map.nationName(e.by)} sanctions ${map.nationName(e.target)}; their treasury "devastated, mildly"`)
    if (e.target === map.you) return story('finance', `${map.nationName(e.by)} sanctions us; imported cheese reclassified as a luxury good`)
    return story('finance', `${map.nationName(e.by)} sanctions ${map.nationName(e.target)}; both sides blame the cheese`)
  },
  taxChanged: (e) => story('press', `Tax rate set to ${TAX_LABELS[keyOf(TAX_SETTINGS, e.setting)]}; citizens "thrilled", says Press Secretary`),
  approvalThreshold: (e) => story('press', APPROVAL_HEADLINES[e.band]),
  phaseChanged: (e) => {
    const text = PHASE_HEADLINES[phaseOf(e.phase)]
    return text === null ? null : story('press', text)
  },
  // Rejections are private toasts, not news.
  commandRejected: () => null,
  outcomeDecided: (e, map) => {
    if (e.winner === NEUTRAL_OWNER) return story('press', 'WAR OVER: both leaders declare victory; historians request an extension')
    if (e.winner === map.you) return story('press', `WAR OVER: ${map.nationName(e.winner)} wins; victory parade to be funded by a victory tax`)
    return story('press', `WAR OVER: ${map.nationName(e.winner)} prevails; our leader to "spend more time with offshore accounts"`)
  },
}

export class HeadlineWriter {
  constructor(private readonly map: WarMapIndex) {}

  /** The headline an event makes, or null if it is not news. */
  write(event: SimEvent): Headline | null {
    const writer = WRITERS[event.kind] as (event: SimEvent, map: WarMapIndex) => Story
    const written = writer(event, this.map)
    return written === null ? null : { tick: event.tick, desk: written.desk, text: written.text }
  }
}
